import { useRef, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { CopyButton } from '@components/copy-button';
import { FormLabelContainer } from '@components/form-label/form-label';
import { IconDownload } from '@components/icons/download';
import { IconPrinter } from '@components/icons/printer';
import { IconShare } from '@components/icons/share';
import { hideModal, Modal, useModal } from '@components/modal';
import { getQrCodeSvg, QrCode } from '@components/qr-code';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { showError } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { printSvg } from '@utils/print';
import { downloadSvg, serializeSvg } from '@utils/svg';

import { JourneyForm } from 'api/journey-forms/requests';
import { config } from 'app-config';

import {
  buildJourneyFormUrl,
  buildStableJourneyFormUrl,
  toDisplayUrl,
} from './build-journey-form-url';
import { ShareFormSlugEditor } from './share-form-slug-editor';

export const useShareJourneyFormModal = () => {
  return useModal(ShareJourneyFormModal, { variant: 'LARGE' });
};

const LINK_FIELD_ID = 'share-form-link';

export const ShareJourneyFormModal = ({ journeyForm }: { journeyForm: JourneyForm }) => {
  const { t } = useScopedTranslate(locale);
  const qrContainerRef = useRef<HTMLDivElement>(null);

  const [currentForm, setCurrentForm] = useState(journeyForm);

  const formsBase = config.FORMS_URL;
  const url = buildJourneyFormUrl(formsBase, currentForm);
  const stableUrl = buildStableJourneyFormUrl(formsBase, currentForm);
  const canEditSlug = Boolean(currentForm.companySlug);

  const handleDownload = () => {
    const svg = getQrCodeSvg(qrContainerRef.current);
    if (!svg) {
      return;
    }
    downloadSvg(svg, `${currentForm.name}-qr.svg`);
  };

  const handlePrint = () => {
    const svg = getQrCodeSvg(qrContainerRef.current);
    if (!svg) {
      return;
    }
    const wasPrinted = printSvg({ title: currentForm.name, svgMarkup: serializeSvg(svg) });
    if (!wasPrinted) {
      showError(t('printBlocked'));
    }
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} icon={<IconShare color="elementBlue200" />} />
      <Modal.Content>
        <Stack space={6}>
          {canEditSlug && <ShareFormSlugEditor form={currentForm} onSaved={setCurrentForm} />}

          <FormLabelContainer id={LINK_FIELD_ID} label={t('linkLabel')}>
            <Stack orientation="horizontal" space={2} align="flex-start">
              <TextField
                id={LINK_FIELD_ID}
                label=""
                value={toDisplayUrl(url)}
                isDisabled
                isFullWidth
              />
              <CopyButton
                variant="primary"
                copyLabel={t('copyLink')}
                getText={() => {
                  return url;
                }}
                hasToast
              />
            </Stack>
            <FullUrl>
              <Text text={url} font="regular14" color="textHelper" />
            </FullUrl>
          </FormLabelContainer>

          <Separator />

          <Card variant="gray" space={4} hasBorder={false}>
            <Stack orientation="horizontal" space={6} align="center">
              <QrCode ref={qrContainerRef} value={stableUrl} label={t('qrTitle')} />
              <Stack space={4}>
                <Stack space={1}>
                  <Text text={t('qrTitle')} font="semibold14" />
                  <Text text={t('qrDescription')} font="regular14" color="textSecondary" />
                </Stack>
                <Stack orientation="horizontal" space={4}>
                  <Button
                    text={t('downloadSvg')}
                    variant="secondary"
                    iconLeft={<IconDownload />}
                    onClick={handleDownload}
                    aria-label={t('downloadSvgLabel')}
                  />
                  <Button
                    text={t('print')}
                    variant="secondary"
                    iconLeft={<IconPrinter />}
                    onClick={handlePrint}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Card>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end">
          <Button text={t('close')} variant="secondary" onClick={hideModal} />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const FullUrl = styled.div({
  wordBreak: 'break-all',
});

const locale = {
  en: {
    title: 'Share form',
    linkLabel: 'Link to share',
    copyLink: 'Copy link',
    qrTitle: 'QR Code / Form ID',
    qrDescription: 'Keeps working even if the slug is edited',
    downloadSvg: 'SVG',
    downloadSvgLabel: 'Download SVG',
    print: 'Print',
    close: 'Close',
    printBlocked:
      'Your browser blocked the print window. Allow pop-ups for this site and try again.',
  },
};
